/**
 * Registration page — v28-3-5
 *
 * Three post-registration paths:
 *   Path 1 — Org-invited user:     welcome banner → navigate('/') after 2s
 *   Path 2 — Individual pro/premium: PayStack redirect (same tab)
 *   Path 3 — Free tier:            navigate('/') unchanged
 *
 * Mobile responsive: max-w-md w-full mx-auto, plan cards grid-cols-1 sm:grid-cols-3.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, Mail, Lock, User, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import subscriptionService from '../services/subscription';
import { fetchPlanPrices } from '../services/planPrices';
import SEO from '../components/SEO';

// ---------------------------------------------------------------------------
// Logo
// ---------------------------------------------------------------------------

const GoldMLogo: React.FC<{ size?: number }> = ({ size = 64 }) => {
  const id = `mg${size}`;
  const pad = size * 0.12;
  const inner = size - pad * 2;
  const pts = [
    [0.23, 0.88], [0.23, 0.40], [0.42, 0.65], [0.50, 0.50],
    [0.58, 0.35], [0.77, 0.62], [0.77, 0.88],
  ].map(([x, y]) => `${(x! * inner + pad).toFixed(2)},${(y! * inner + pad).toFixed(2)}`).join(' ');
  const r = (size * 0.12).toFixed(2);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`${id}-stroke`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F5D78E" />
          <stop offset="50%" stopColor="#C9A84C" />
          <stop offset="100%" stopColor="#8B6914" />
        </linearGradient>
        <linearGradient id={`${id}-bg`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1a1035" />
          <stop offset="100%" stopColor="#0d0820" />
        </linearGradient>
      </defs>
      <rect width={size} height={size} rx={r} fill={`url(#${id}-bg)`} />
      <rect x="0.5" y="0.5" width={size - 1} height={size - 1} rx={r} stroke="#C9A84C" strokeWidth="0.8" opacity="0.4" />
      <polyline points={pts} stroke={`url(#${id}-stroke)`} strokeWidth={size * 0.065} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Plan card
// ---------------------------------------------------------------------------

type Plan = 'free' | 'pro' | 'premium';

interface PlanCardProps {
  tier: Plan;
  selected: boolean;
  onSelect: () => void;
  priceDisplay: string;
  priceLoading?: boolean;
}

const PLAN_LABELS: Record<Plan, string> = { free: 'Free', pro: 'Pro', premium: 'Premium' };
const PLAN_DESC: Record<Plan, string> = {
  free: 'Basic portfolio tracking',
  pro: 'Advanced analytics + AI',
  premium: 'Full AI suite + priority',
};

function PlanCard({ tier, selected, onSelect, priceDisplay, priceLoading }: PlanCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-3 rounded-lg border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 ${
        selected
          ? 'border-teal-500 bg-teal-500/10'
          : 'border-slate-600 bg-slate-700/30 hover:border-slate-500'
      }`}
    >
      <p className="text-sm font-semibold text-white">{PLAN_LABELS[tier]}</p>
      {priceLoading && tier !== 'free' ? (
        <div className="h-3 w-20 bg-slate-600 animate-pulse rounded mt-0.5" />
      ) : (
        <p className="text-xs text-teal-400 font-medium mt-0.5">{priceDisplay}</p>
      )}
      <p className="text-xs text-slate-400 mt-1 leading-tight">{PLAN_DESC[tier]}</p>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<Plan>('free');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [initError, setInitError] = useState('');

  const { data: planPrices, isLoading: pricesLoading } = useQuery({
    queryKey: ['plan-prices'],
    queryFn: fetchPlanPrices,
    staleTime: 60_000,
    retry: 1,
  });

  // Free tier is a UI-fixed label, not an admin-configurable price.
  // For pro/premium, an empty string + priceLoading=true triggers the
  // skeleton in PlanCard when neither the API nor the cached value
  // produced a display string.
  const getPriceDisplay = (tier: Plan): string => {
    if (tier === 'free') return '$0.00/mo';
    return planPrices?.[tier]?.display ?? '';
  };
  const showPriceSkeleton = pricesLoading || !planPrices;

  // Capture the plan at submission time so retry uses the correct plan
  const submittedPlanRef = useRef<'pro' | 'premium' | null>(null);

  // Org welcome state (Path 1)
  const [orgWelcome, setOrgWelcome] = useState<{ orgName: string | null; tier: string | null } | null>(null);

  // Cleanup Path 1 redirect timer on unmount
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const regResult = await register(email, password, fullName);

      // ── Path 1: Org-invited user ───────────────────────────────────────────
      if (regResult.invite_accepted) {
        setOrgWelcome({
          orgName: regResult.org_name,
          tier: regResult.invite_seat_tier,
        });
        redirectTimerRef.current = setTimeout(() => navigate('/'), 2000);
        return;
      }

      // ── Path 2: Individual paid plan ──────────────────────────────────────
      if (selectedPlan === 'pro' || selectedPlan === 'premium') {
        const planToCharge = selectedPlan;
        submittedPlanRef.current = planToCharge;
        setInitLoading(true);
        try {
          const { url } = await subscriptionService.initialize(planToCharge);
          window.location.href = url;
        } catch {
          setInitLoading(false);
          setInitError('Could not start payment. Please try again.');
        } finally {
          setLoading(false);
        }
        return;
      }

      // ── Path 3: Free tier ─────────────────────────────────────────────────
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Path 1 confirmation banner ──────────────────────────────────────────
  if (orgWelcome) {
    const tierLabel = orgWelcome.tier
      ? orgWelcome.tier.charAt(0).toUpperCase() + orgWelcome.tier.slice(1)
      : null;
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md mx-auto px-4">
          <div className="bg-teal-500/10 border border-teal-500 rounded-xl p-6 text-center space-y-3">
            <CheckCircle className="w-12 h-12 text-teal-400 mx-auto" />
            <h2 className="text-xl font-bold text-white">
              Welcome{orgWelcome.orgName ? ` to ${orgWelcome.orgName}` : ''}!
            </h2>
            <p className="text-slate-300 text-sm">
              Your account has been provisioned{tierLabel ? ` as a ${tierLabel} member` : ''}.
            </p>
            <p className="text-slate-400 text-xs">You'll be redirected shortly…</p>
            <div className="flex justify-center pt-2">
              <div className="w-5 h-5 border-2 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── PayStack init loading state ──────────────────────────────────────────
  if (initLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md mx-auto px-4 text-center space-y-4">
          <Loader2 className="w-10 h-10 text-teal-400 mx-auto animate-spin" />
          <p className="text-slate-300 text-sm">Preparing payment…</p>
        </div>
      </div>
    );
  }

  const isPaidPlan = selectedPlan === 'pro' || selectedPlan === 'premium';
  const submitLabel = isPaidPlan ? 'Continue to payment' : 'Create Account';

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4 py-8 sm:px-6">
      <SEO
        title="Create Account"
        description="Join My Investments — the AI-powered platform for traders and investors. Access real-time market data, AI signals, and portfolio tools."
        canonical="https://my-investments.io/register"
      />
      <div className="w-full max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <GoldMLogo size={64} />
          </div>
          <h1 className="text-2xl font-bold text-white">Create Account</h1>
          <p className="text-slate-400 mt-2 text-sm">Start tracking your investments with My Investments</p>
        </div>

        {/* Form card */}
        <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-6 lg:p-8">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {error && (
              <div className="flex items-center gap-2 text-red-400 bg-red-400/10 px-4 py-3 rounded-lg text-sm w-full">
                <AlertCircle size={16} className="shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Full name */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                  required
                />
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Confirm Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                  required
                />
              </div>
            </div>

            {/* Plan selection */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Choose a Plan</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <PlanCard tier="free" selected={selectedPlan === 'free'} onSelect={() => setSelectedPlan('free')} priceDisplay={getPriceDisplay('free')} priceLoading={false} />
                <PlanCard tier="pro" selected={selectedPlan === 'pro'} onSelect={() => setSelectedPlan('pro')} priceDisplay={getPriceDisplay('pro')} priceLoading={showPriceSkeleton} />
                <PlanCard tier="premium" selected={selectedPlan === 'premium'} onSelect={() => setSelectedPlan('premium')} priceDisplay={getPriceDisplay('premium')} priceLoading={showPriceSkeleton} />
              </div>
              {isPaidPlan && (
                <p className="text-xs text-slate-400 mt-2">
                  A payment page will open after registration to collect your card details.
                </p>
              )}
            </div>

            {/* PayStack init error with retry */}
            {initError && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-red-400 bg-red-400/10 px-4 py-3 rounded-lg text-sm w-full">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{initError}</span>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    const plan = submittedPlanRef.current;
                    if (!plan) return;
                    setInitError('');
                    setInitLoading(true);
                    try {
                      const { url } = await subscriptionService.initialize(plan);
                      window.location.href = url;
                    } catch {
                      setInitLoading(false);
                      setInitError('Could not start payment. Please try again.');
                    }
                  }}
                  className="w-full sm:w-auto px-4 py-2 bg-slate-700 text-white rounded-lg text-sm hover:bg-slate-600 transition-colors"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
              ) : (
                <>
                  <UserPlus size={18} />
                  {submitLabel}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-400 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-teal-400 hover:text-teal-300 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
